import { defineStore } from "pinia";
import { computed } from "vue";
import { pickBy } from "lodash-es";
import { useSceneNodesStore } from "../scene/sceneNodes.store";
import type { SceneNodeItem } from "../scene/SceneNodeItem";
import type { SceneShapeData } from "./sceneShapeData";
import { createNewShapePath } from "./sceneShapeFunctions";
import type { Subset } from "@/helpers/utility/Type";

export const shapeSizeDefaults: { width: number; height: number } = {
  width: 100,
  height: 100
};
export const shapeDefaults: Omit<SceneShapeData, "title" | "path"> = {
  fill: "#FFFFFF",
  stroke: "#000000",
  strokeWidth: 1,
  strokeStyle: "solid",
  shapeType: "rectangle",
  radius: 10
};

export const useSceneShapeStore = defineStore("model/sceneShapes", () => {
  const sceneNodeStore = useSceneNodesStore();

  const defaults = shapeDefaults;
  const sizeDefaults = shapeSizeDefaults;

  function isShapeItem(item: SceneNodeItem): item is SceneNodeItem<"shape"> {
    return item.dataType === "shape";
  }

  async function create(options: Subset<SceneNodeItem<"shape">>, data: Partial<SceneShapeData> = {}) {
    const filledData: SceneShapeData = {
      fill: options.data?.fill ?? data.fill ?? shapeDefaults.fill,
      stroke: options.data?.stroke ?? data.stroke ?? shapeDefaults.stroke,
      strokeWidth: options.data?.strokeWidth ?? data.strokeWidth ?? shapeDefaults.strokeWidth,
      strokeStyle: options.data?.strokeStyle ?? data.strokeStyle ?? shapeDefaults.strokeStyle,
      shapeType: options.data?.shapeType ?? data.shapeType ?? shapeDefaults.shapeType,
      radius: options.data?.radius ?? data.radius ?? shapeDefaults.radius,
      path: options.data?.path ?? data.path ?? ""
    };

    return await sceneNodeStore.create({
      dataType: "shape",
      ...options,
      data: filledData,
    });
  }

  const items = computed(() => {
    return pickBy(sceneNodeStore.items, isShapeItem) as Record<number, SceneNodeItem<"shape">>;
  });

  const byUid = computed(() => {
    return pickBy(sceneNodeStore.byUid, isShapeItem) as Record<string, SceneNodeItem<"shape">>;
  });

  const shapeTypes = [
    "rectangle",
    "roundedRectangle",
    "circle",
    "triangle",
    "polygon"
  ] as SceneShapeData["shapeType"][];

  function convertShapeType(id: number, newShapeType: SceneShapeData["shapeType"]) {
    const item = items.value[id];
    if (!item) {
      throw new Error(`Item with id ${id} not found.`);
    }

    const shapeData = item.data;
    const width = item.width || shapeSizeDefaults.width;
    const height = item.height || shapeSizeDefaults.height;

    let path = shapeData.path;

    if (newShapeType != "polygon") {
      path = createNewShapePath(
        newShapeType, 
        width, 
        height, 
        shapeData.radius || shapeDefaults.radius
      );
    }

    sceneNodeStore.update({
      id,
      data: {
        ...shapeData,
        shapeType: newShapeType,
        path: path
      }
    });
  }

  function $reset() {
    // handled by sceneNodeStore.
    return;
  }

  return {
    defaults,
    sizeDefaults,
    items,
    byUid,
    shapeTypes,
    create,
    convertShapeType,
    $reset
  }
});
